import { useId, useState, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import styles from './primitives.module.css';

// Форм-примитивы Vision — адаптация FieldStub / PrimaryBtn / пилюль из
// PostFlow прототипа партнёра (логин-экрана в прототипе нет — экраны V1.3
// собираются из этих же готовых паттернов, lightweight-путь design-workflow).

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  inputMode,
  name,
  maxLength,
}: {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'url';
  name?: string;
  maxLength?: number;
}): JSX.Element {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <input
        className={styles.input}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        name={name}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

// Поле пароля с тумблером видимости (доводка приёмки V1: «не вижу, что ввёл»).
// Label связан с input через htmlFor/id, а не обёрткой: иначе кнопка-тумблер
// тоже попадает под label (labelable-элемент) и getByLabelText/скринридеры
// видят два контрола. Тумблер — type="button", чтобы Enter в поле по-прежнему
// сабмитил форму; aria-pressed + подпись из i18n — для скринридеров.
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  name,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: 'current-password' | 'new-password';
  name?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const [visible, setVisible] = useState(false);
  const toggleLabel = visible ? t('form.password.hide') : t('form.password.show');
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.fieldLabel}>
        {label}
      </label>
      <span className={styles.passwordWrap}>
        <input
          id={id}
          className={`${styles.input} ${styles.passwordInput}`}
          type={visible ? 'text' : 'password'}
          value={value}
          autoComplete={autoComplete}
          name={name}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className={styles.passwordToggle}
          onClick={() => setVisible((v) => !v)}
          aria-label={toggleLabel}
          title={toggleLabel}
          aria-pressed={visible}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </span>
    </div>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  name,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  name?: string;
}): JSX.Element {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <select
        className={styles.select}
        value={value}
        name={name}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function PrimaryButton({
  children,
  disabled,
  type = 'submit',
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  type?: 'submit' | 'button';
  onClick?: () => void;
}): JSX.Element {
  return (
    <button className={styles.primary} type={type} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

/** Второстепенное действие («Изменить номер», «Отправить код ещё раз»). */
export function GhostButton({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button className={styles.ghost} type="button" disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

/** Ряд взаимоисключающих пилюль (способ входа, канал кода) — стиль вкладок прототипа. */
export function ChoiceRow<TValue extends string | number>({
  value,
  onChange,
  options,
  label,
}: {
  value: TValue;
  onChange: (value: TValue) => void;
  /** `disabled` — вариант есть, но сейчас недоступен (напр. WhatsApp без телефона организации). */
  options: { value: TValue; label: string; disabled?: boolean }[];
  label: string;
}): JSX.Element {
  return (
    <div className={styles.pillRow} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          disabled={option.disabled}
          className={`${styles.pill} ${option.value === value ? styles.pillActive : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Чекбокс с подписью (флаг «телефон есть в WhatsApp», №22) — стиль подписей FieldStub. */
export function CheckboxField({
  label,
  checked,
  onChange,
  disabled,
  name,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  name?: string;
}): JSX.Element {
  return (
    <label className={`${styles.checkbox} ${disabled ? styles.checkboxDisabled : ''}`}>
      <input
        className={styles.checkboxInput}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        name={name}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

/** Инлайн-ошибка формы (Vision без тостера — ошибки живут в форме). */
export function FormError({ children }: { children: ReactNode }): JSX.Element | null {
  if (!children) return null;
  return (
    <div className={styles.error} role="alert">
      {children}
    </div>
  );
}

/** Поясняющий текст шага (стиль gisHint/smsHint прототипа). */
export function Hint({ children }: { children: ReactNode }): JSX.Element {
  return <p className={styles.hint}>{children}</p>;
}

/** Многострочное поле (описание объявления, причина жалобы) — FieldStub tall прототипа. */
export function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  name,
  maxLength,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  name?: string;
  maxLength?: number;
  rows?: number;
}): JSX.Element {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <textarea
        className={styles.textarea}
        value={value}
        placeholder={placeholder}
        name={name}
        maxLength={maxLength}
        rows={rows}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

/**
 * Поле выбора файла (баннер рекламы, V1.4). Нативный `<input type=file>` в
 * обёртке FieldStub: подпись + подсказка формата; сам файл наружу отдаём
 * сразу в `onSelect` — загрузка и превью живут в форме. Значение input не
 * контролируем (браузер не даёт), поэтому после загрузки выбор того же файла
 * повторно не сработает — на смену баннера форма перерисовывает поле.
 */
export function FileField({
  label,
  accept,
  onSelect,
  disabled,
  hint,
  name,
}: {
  label: string;
  accept?: string;
  onSelect: (file: File) => void;
  disabled?: boolean;
  hint?: ReactNode;
  name?: string;
}): JSX.Element {
  // htmlFor/id, а не обёртка: подсказка формата не должна попадать в
  // accessible name поля (getByLabelText / скринридеры видят только подпись).
  const id = useId();
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.fieldLabel}>
        {label}
      </label>
      <input
        id={id}
        className={styles.fileInput}
        type="file"
        accept={accept}
        disabled={disabled}
        name={name}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onSelect(file);
        }}
      />
      {hint && <span className={styles.fileHint}>{hint}</span>}
    </div>
  );
}
